import { useTranslate } from 'core/hooks/useTranslate';
import { useCallback, useState } from 'react';

type UseDeleteModuleDialogProps = {
  type: 'module' | 'submodule';
  onSuccess: () => void;
  onClose: () => void;
  isLoading?: boolean;
};

type UseDeleteModuleDialogReturn = {
  t: (key: string) => string;
  title: string;
  questionText: string;
  checkboxLabel: string;
  disclaimerText: string;
  buttonText: string;
  isChecked: boolean;
  handleCheckboxChange: () => void;
  handleConfirm: () => void;
  handleClose: () => void;
  isDisabled: boolean;
};

function useDeleteModuleDialog({ type, onClose, onSuccess, isLoading = false }: UseDeleteModuleDialogProps): UseDeleteModuleDialogReturn {
  const { t } = useTranslate();
  const [isChecked, setIsChecked] = useState(false);
  const isModule = type === 'module';
  const title = t('organize-content:Delete content');
  const questionText = isModule ? t('organize-content:Delete module confirmation question') : t('organize-content:Delete submodule confirmation question');
  const checkboxLabel = t('organize-content:Yes I am sure');
  const disclaimerText = t('organize-content:Delete disclaimer');
  const buttonText = t('organize-content:Yes, delete');

  const handleCheckboxChange = useCallback(() => {
    setIsChecked((prev) => !prev);
  }, []);

  const handleConfirm = useCallback(() => {
    if (isChecked) {
      onSuccess();
      setIsChecked(false);
    }
  }, [isChecked, onSuccess]);

  const handleClose = useCallback(() => {
    setIsChecked(false);
    onClose();
  }, [onClose]);

  const isDisabled = !isChecked || isLoading;

  return {
    t,
    title,
    questionText,
    checkboxLabel,
    disclaimerText,
    buttonText,
    isChecked,
    handleCheckboxChange,
    handleConfirm,
    handleClose,
    isDisabled,
  };
}

export default useDeleteModuleDialog;
